import { FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Send, Globe2, Paperclip, FileText, X, Pin, Trash2, RotateCcw, ArrowRight } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import { MarkdownView } from '@/components/app/MarkdownView';
import {
  useRunAgent, useAiResults, usePinAiResult, useDeleteAiResult,
  type AgentResult, type AiSavedResult,
} from '@/lib/aiAgents';
import { useResidency, useAssess, type ResidencyEmployee, type AssessResult } from '@/lib/border-buddy';
import { countryFlag } from '@/lib/countries';

/**
 * Dedicated UI for the flagship "Border Buddy" agent — the
 * cross-jurisdiction compliance Q&A that's the spec's #1
 * differentiator vs SmartHR / BambooHR / HiBob. It's a thin shell
 * over the generic ai-agent endpoint, but pre-selected to the
 * `compliance` agent + reasoning model + with sample queries that
 * showcase the treaty knowledge base. Same file-attach contract as
 * AgentsPage — server parses PDFs/DOCX/images to text.
 */

const SAMPLE_QUERIES: Array<{ id: string; flag: string; question: string }> = [
  {
    id: 'jp-de-secondment',
    flag: '🇯🇵→🇩🇪',
    question:
      'Tanaka-san is being seconded from Tokyo to Berlin from April 2026 to April 2029 (3 years). Which social-security regime applies, and what filings do we need on both sides?',
  },
  {
    id: 'us-jp-pe',
    flag: '🇺🇸→🇯🇵',
    question:
      'We hired a US-resident sales engineer who will work remotely from Osaka for ~120 days/year. Does this create a Permanent Establishment risk in Japan for our US parent?',
  },
  {
    id: 'uk-de-telework',
    flag: '🇬🇧↔🇩🇪',
    question:
      'A UK employee wants to work 40% of the time from Berlin and 60% from London. What does the post-Brexit cross-border arrangement say about social-security coverage?',
  },
  {
    id: 'jp-yearly',
    flag: '🇯🇵',
    question:
      'Walk me through the JP year-end adjustment (年末調整) filing chain — which forms, which deadlines, who at the company is responsible.',
  },
];

const ALLOWED_EXT = ['.pdf', '.docx', '.txt', '.md', '.png', '.jpg', '.jpeg'];
const MAX_FILE_BYTES = 10 * 1024 * 1024;

export function BorderBuddyPage() {
  const { t } = useTranslation();
  const run = useRunAgent();
  const savedResults = useAiResults('compliance');
  const [question, setQuestion] = useState('');
  const [result, setResult] = useState<AgentResult | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  /** Reopen a saved result into the answer pane without re-running. */
  const reopen = (r: AiSavedResult) => {
    setQuestion(r.input_text);
    setResult({
      answer: r.output_text,
      model: r.model ?? '',
      inputTokens: 0,
      outputTokens: 0,
      costCents: 0,
      citedRecords: r.cited_record_ids ?? [],
      resultId: r.id,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onPickFile = (f: File | undefined | null) => {
    setFileError(null);
    if (!f) return;
    const ext = '.' + (f.name.split('.').pop() ?? '').toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      setFileError(`Unsupported file type. Allowed: ${ALLOWED_EXT.join(', ')}.`);
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setFileError(`File is too large (max ${Math.round(MAX_FILE_BYTES / 1024 / 1024)} MB).`);
      return;
    }
    setFile(f);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    onPickFile(e.dataTransfer.files?.[0]);
  };

  const submit = async (q: string) => {
    if (!q.trim()) return;
    setQuestion(q);
    setResult(null);
    const r = await run.mutateAsync({
      agentKind: 'compliance',
      question: q,
      reasoning: true, // server-side hint to use the reasoning tier for cross-border questions
      file: file ?? undefined,
    });
    setResult(r);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit(question);
  };

  return (
    <AppShell>
      <div
        className="px-6 md:px-10 py-8 max-w-5xl mx-auto space-y-6"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        {/* Hero card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-3xl glass-strong shadow-card-lift p-8"
        >
          <div aria-hidden className="pointer-events-none absolute -top-24 -end-24 h-72 w-72 rounded-full bg-gradient-to-br from-indigo-400/40 to-fuchsia-400/30 blur-3xl" />
          <div aria-hidden className="pointer-events-none absolute -bottom-24 -start-24 h-72 w-72 rounded-full bg-gradient-to-br from-sky-300/30 to-emerald-300/20 blur-3xl" />
          <div className="relative">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-primary">
              <Sparkles className="h-3 w-3" /> {t('borderBuddy.flagBadge')}
            </span>
            <h1 className="display serif-jp mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-balance">
              {t('borderBuddy.title')}
            </h1>
            <p className="mt-2 text-base md:text-lg text-muted-foreground max-w-2xl text-pretty">
              {t('borderBuddy.subtitle')}
            </p>
          </div>
        </motion.div>

        {/* Residency / PE tracker + structured assessment (attendance-grounded) */}
        <ResidencySection />

        {/* Cross-navigation links */}
        <div className="flex flex-wrap gap-2">
          <Link
            to="/app/agents"
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:border-primary/50 hover:text-primary transition shadow-soft"
          >
            <ArrowRight className="h-3 w-3" />
            {t('borderBuddy.goToAgents', { defaultValue: 'All AI Agents' })}
          </Link>
          <Link
            to="/app/compliance"
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:border-primary/50 hover:text-primary transition shadow-soft"
          >
            <ArrowRight className="h-3 w-3" />
            {t('borderBuddy.goToCompliance', { defaultValue: 'Compliance' })}
          </Link>
          <Link
            to="/app/year-end"
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:border-primary/50 hover:text-primary transition shadow-soft"
          >
            <ArrowRight className="h-3 w-3" />
            {t('borderBuddy.goToYearEnd', { defaultValue: 'Year-End' })}
          </Link>
        </div>

        {/* Ask form */}
        <form
          onSubmit={onSubmit}
          className={`rounded-2xl border bg-card p-4 shadow-soft transition ${dragOver ? 'border-primary ring-2 ring-primary/20' : 'border-border'}`}
        >
          <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
            {t('borderBuddy.askLabel')}
          </label>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={3}
            placeholder={t('borderBuddy.placeholder')}
            className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-y"
          />

          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_EXT.join(',')}
            className="hidden"
            onChange={(e) => onPickFile(e.target.files?.[0])}
          />

          {file ? (
            <div className="mt-3 flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
              <FileText className="h-4 w-4 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-foreground truncate">{file.name}</p>
                <p className="text-[10px] text-muted-foreground">{formatBytes(file.size)}</p>
              </div>
              <button
                type="button"
                onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-muted transition"
                aria-label={t('borderBuddy.removeFile')}
              >
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition"
            >
              <Paperclip className="h-3.5 w-3.5" />
              {t('borderBuddy.attach')} <span className="text-muted-foreground/70">({t('borderBuddy.attachHint')})</span>
            </button>
          )}

          {fileError && (
            <p className="mt-2 text-xs text-rose-600">{fileError}</p>
          )}

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-[11px] text-muted-foreground flex-1 min-w-0">
              {t('borderBuddy.modelNote')}
            </p>
            <button
              type="submit"
              disabled={run.isPending || !question.trim()}
              className="gradient-primary inline-flex h-10 items-center gap-2 px-5 rounded-xl text-white text-sm font-semibold shadow-glow hover:shadow-glow-pink disabled:opacity-60 transition shrink-0"
            >
              {run.isPending ? (
                <>
                  <span className="h-3.5 w-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  {file ? t('borderBuddy.reading') : t('borderBuddy.thinking')}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  {t('borderBuddy.ask')}
                </>
              )}
            </button>
          </div>
          {run.isError && (
            <p className="mt-3 text-xs text-rose-600">
              {(run.error as { response?: { data?: { message?: string } } })?.response?.data?.message
                || (run.error as Error)?.message
                || 'Run failed — try again or check the server.'}
            </p>
          )}
        </form>

        {/* Sample queries */}
        {!result && !run.isPending && (
          <section>
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-3">
              {t('borderBuddy.samplesTitle')}
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {SAMPLE_QUERIES.map((s, i) => (
                <motion.button
                  key={s.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => void submit(s.question)}
                  className="group text-start rounded-2xl border border-border bg-card p-4 shadow-soft hover:shadow-card-lift hover:border-primary/40 transition"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-base">{s.flag}</span>
                    <Globe2 className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition" />
                  </div>
                  <p className="text-sm text-foreground/85 line-clamp-3">{s.question}</p>
                </motion.button>
              ))}
            </div>
          </section>
        )}

        {/* Result */}
        {result && (
          <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl border border-border bg-card p-6 shadow-soft"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="grid h-7 w-7 place-items-center rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white text-xs">✦</span>
              <p className="text-sm font-semibold">{t('borderBuddy.answer')}</p>
              <span className="ms-auto num text-[10px] text-muted-foreground">
                ${(result.costCents / 100).toFixed(4)}
              </span>
            </div>
            <MarkdownView>{result.answer || ''}</MarkdownView>
            {result.citedRecords.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">
                  {t('borderBuddy.citations')}
                </p>
                <ul className="flex flex-wrap gap-1.5">
                  {result.citedRecords.map((r) => (
                    <li key={r} className="text-[10px] num bg-muted text-foreground/80 px-2 py-1 rounded-md">{r}</li>
                  ))}
                </ul>
              </div>
            )}
          </motion.section>
        )}

        {/* Saved compliance results — recoverable history */}
        <BorderBuddySavedPanel savedResults={savedResults.data ?? []} onReopen={reopen} />
      </div>
    </AppShell>
  );
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Saved compliance results for Border Buddy — shows past Q&A answers so
 * users can recover answers without re-running (and re-spending).
 */
function BorderBuddySavedPanel({
  savedResults,
  onReopen,
}: {
  savedResults: AiSavedResult[];
  onReopen: (r: AiSavedResult) => void;
}) {
  const { t } = useTranslation();
  const pin = usePinAiResult();
  const del = useDeleteAiResult();

  if (savedResults.length === 0) return null;

  return (
    <section className="rounded-3xl border border-border bg-card p-6 shadow-soft">
      <h3 className="text-sm font-semibold mb-3">
        {t('borderBuddy.savedResults', { defaultValue: 'Saved answers · 保存済み回答' })}
      </h3>
      <ul className="divide-y divide-border -mx-2">
        {savedResults.slice(0, 20).map((r) => (
          <li key={r.id} className="px-2 py-2.5">
            <div className="flex items-start gap-2">
              <button
                type="button"
                onClick={() => onReopen(r)}
                className="flex-1 min-w-0 text-start group"
                title={t('borderBuddy.reopenSaved', { defaultValue: 'Reopen this answer' })}
              >
                <p className="text-xs font-medium text-foreground/85 truncate group-hover:text-primary transition">
                  {r.pinned && '📌 '}{r.title}
                </p>
                <p className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</p>
              </button>
              <button
                type="button"
                onClick={() => onReopen(r)}
                className="text-muted-foreground hover:text-primary transition"
                title={t('borderBuddy.reopenSaved', { defaultValue: 'Reopen this answer' })}
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => pin.mutate({ id: r.id, pinned: !r.pinned })}
                className={`transition hover:text-primary ${r.pinned ? 'text-primary' : 'text-muted-foreground'}`}
                title={r.pinned
                  ? t('borderBuddy.unpin', { defaultValue: 'Unpin' })
                  : t('borderBuddy.pin', { defaultValue: 'Pin' })}
              >
                <Pin className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => del.mutate(r.id)}
                className="text-muted-foreground hover:text-rose-600 transition"
                title={t('borderBuddy.deleteSaved', { defaultValue: 'Delete' })}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ───────────────── Residency & PE (attendance-grounded) ─────────────────

const STATE_CLS: Record<'ok' | 'approaching' | 'exceeded', string> = {
  ok: 'text-ink-500',
  approaching: 'text-amber-600 font-semibold',
  exceeded: 'text-warn-600 font-bold',
};

function ResidencySection() {
  const { t } = useTranslation();
  const residency = useResidency();
  const r = residency.data;

  return (
    <section className="rounded-2xl border border-ink-200 bg-white p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Globe2 className="h-4 w-4 text-accent-600" />
        <h2 className="text-sm font-semibold text-ink-800">{t('residency.title', { defaultValue: 'Residency & PE tracker' })}</h2>
        {r && r.flagged > 0 && <span className="text-[10px] font-semibold uppercase tracking-wide text-warn-700 bg-warn-50 border border-warn-500/30 rounded px-1.5 py-0.5">{r.flagged} {t('residency.flagged', { defaultValue: 'at risk' })}</span>}
      </div>
      <p className="text-xs text-ink-500">{t('residency.hint', { threshold: r?.threshold ?? 183, defaultValue: 'Worked-days per country over the last 12 months, from attendance. {{threshold}}+ days in a foreign country can trigger tax residency / permanent-establishment exposure.' })}</p>

      {residency.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}
      {r && r.employees.length === 0 && <p className="text-sm text-ink-500">{t('residency.none', { defaultValue: 'No cross-border presence detected. Tag work location at clock-in to track this.' })}</p>}
      {r && r.employees.length > 0 && (
        <div className="space-y-2">
          {r.employees.map((e: ResidencyEmployee) => (
            <div key={e.employeeId} className="rounded-lg border border-ink-100 p-3">
              <p className="text-sm font-medium text-ink-900">{e.name} <span className="text-[10px] text-ink-400">{t('residency.home', { defaultValue: 'home' })} {countryFlag(e.homeCountry)} {e.homeCountry}</span></p>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 num text-xs">
                {e.presence.map((p) => (
                  <span key={p.country} className={STATE_CLS[p.state]}>
                    {countryFlag(p.country)} {p.country}: {p.days}d{p.state === 'exceeded' ? ' ⚠' : p.state === 'approaching' ? ' •' : ''}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <AssessTool />
    </section>
  );
}

function AssessTool() {
  const { t } = useTranslation();
  const assess = useAssess();
  const [home, setHome] = useState('JP');
  const [host, setHost] = useState('US');
  const [days, setDays] = useState(183);
  const [authority, setAuthority] = useState(false);
  const [fixedPlace, setFixedPlace] = useState(false);
  const [res, setRes] = useState<AssessResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    setErr(null); setRes(null);
    try {
      const r = await assess.mutateAsync({ homeCountry: home, hostCountry: host, daysPresent: days, hasContractAuthority: authority, fixedPlace });
      setRes(r);
    } catch (e: unknown) {
      setErr(((e as { response?: { data?: { message?: string } } })?.response?.data?.message) || (e as Error).message);
    }
  }

  return (
    <div className="rounded-lg border border-ink-100 bg-ink-50/40 p-3">
      <p className="text-xs font-semibold text-ink-700">{t('residency.assessTitle', { defaultValue: 'Quick assessment' })}</p>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <label className="block"><span className="block text-[10px] uppercase tracking-wide text-ink-500 font-semibold mb-0.5">{t('residency.from', { defaultValue: 'Home' })}</span>
          <input value={home} onChange={(e) => setHome(e.target.value.toUpperCase().slice(0, 2))} className="input w-16 num" /></label>
        <label className="block"><span className="block text-[10px] uppercase tracking-wide text-ink-500 font-semibold mb-0.5">{t('residency.to', { defaultValue: 'Host' })}</span>
          <input value={host} onChange={(e) => setHost(e.target.value.toUpperCase().slice(0, 2))} className="input w-16 num" /></label>
        <label className="block"><span className="block text-[10px] uppercase tracking-wide text-ink-500 font-semibold mb-0.5">{t('residency.days', { defaultValue: 'Days present' })}</span>
          <input type="number" min={0} max={366} value={days} onChange={(e) => setDays(Number(e.target.value))} className="input w-24 num" /></label>
        <label className="flex items-center gap-1.5 text-xs text-ink-700 pb-2"><input type="checkbox" checked={authority} onChange={(e) => setAuthority(e.target.checked)} className="accent-accent-500" />{t('residency.authority', { defaultValue: 'Signs contracts' })}</label>
        <label className="flex items-center gap-1.5 text-xs text-ink-700 pb-2"><input type="checkbox" checked={fixedPlace} onChange={(e) => setFixedPlace(e.target.checked)} className="accent-accent-500" />{t('residency.fixedPlace', { defaultValue: 'Fixed office' })}</label>
        <button onClick={run} disabled={assess.isPending} className="px-3 py-2 rounded-lg bg-accent-500 text-white text-xs font-semibold hover:bg-accent-600 disabled:opacity-50">{assess.isPending ? t('residency.assessing', { defaultValue: 'Assessing…' }) : t('residency.assess', { defaultValue: 'Assess' })}</button>
      </div>
      {err && <p className="mt-2 text-xs text-warn-600">{err}</p>}
      {res && (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap gap-2">
            <Flag on={res.flags.residencyShift} label={t('residency.shift', { defaultValue: 'Residency shift' })} />
            <Flag on={res.flags.peRisk} label={t('residency.pe', { defaultValue: 'PE risk' })} />
            {res.flags.totalizationTreaty && <span className="text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 bg-blue-50 text-blue-700">{t('residency.totalization', { defaultValue: 'Totalization treaty' })}</span>}
          </div>
          {res.narrative && <div className="rounded-lg bg-white border border-ink-100 p-2.5"><MarkdownView>{res.narrative}</MarkdownView></div>}
          {res.citations.length > 0 && (
            <details className="text-[11px] text-ink-500">
              <summary className="cursor-pointer font-semibold">{t('residency.citations', { defaultValue: 'Citations' })} ({res.citations.length})</summary>
              <ul className="mt-1 space-y-1">
                {res.citations.map((c, i) => <li key={i}><span className="font-mono text-ink-600">{c.topic}</span> — {c.summary.slice(0, 160)}… <span className="text-ink-400">({c.source})</span></li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function Flag({ on, label }: { on: boolean; label: string }) {
  return <span className={`text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 ${on ? 'bg-warn-50 text-warn-700 border border-warn-500/30' : 'bg-emerald-50 text-emerald-700'}`}>{on ? '⚠ ' : '✓ '}{label}</span>;
}
